import { env } from "process"

import type { HttpRequestConfig, LogLevel } from "./types"

// 严格判断客户端环境：检查 window 对象是否存在
export const isClient = typeof window !== "undefined" && typeof window.document !== "undefined"
export const isServer = !isClient

export async function showErrorMessage(message: string) {
  if (isClient) {
    try {
      const { message: antdMessage } = await import("antd")
      antdMessage.error(message)
    } catch (_error) {
      console.error("Antd message error:", message)
    }
  }
}

export function getCookie(name: string): string | null {
  if (!isClient) return null
  try {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null
    }
  } catch {
    return null
  }
  return null
}

export function getClientLangeuage() {
  if (isClient) {
    try {
      const storageLang = localStorage.getItem("lang") || localStorage.getItem("locale")
      if (storageLang) return storageLang
      const cookieLang = getCookie("lang") || getCookie("locale")
      if (cookieLang) return cookieLang
      const browserLang = navigator.language || navigator.languages?.[0]
      if (browserLang) {
        return browserLang.split("-")[0] || null
      }
    } catch {
      return null
    }
  }
}

export function getLanguage(config: HttpRequestConfig): string | null {
  if (config.lang) return config.lang
  if (isClient) {
    return getClientLangeuage() || null
  }
  return null
}

/**
 * 从完整 URL 中提取路径部分（不包含 query 参数）
 * 用于日志显示，避免 URL 过长
 */
function extractUrlPath(url: string): string {
  try {
    // 如果是完整 URL（包含协议），使用 URL 对象解析
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const urlObj = new URL(url)
      return urlObj.pathname
    }
    // 如果是相对路径，提取路径部分（? 之前的部分）
    const queryIndex = url.indexOf("?")
    return queryIndex !== -1 ? url.substring(0, queryIndex) : url
  } catch {
    // 解析失败时，尝试简单提取路径部分
    const queryIndex = url.indexOf("?")
    return queryIndex !== -1 ? url.substring(0, queryIndex) : url
  }
}

/**
 * 异步日志输出，避免阻塞主线程
 * 使用 setImmediate 将日志输出推迟到下一个事件循环，不影响当前请求处理
 */
function asyncLog(fn: () => void): void {
  if (typeof setImmediate !== "undefined") {
    setImmediate(fn)
  } else {
    // 降级到 setTimeout，兼容性更好
    setTimeout(fn, 0)
  }
}

export function addQueryParams(url: string, params: Record<string, string>): string {
  const urlObj = new URL(url, "http://dummy.com")
  Object.entries(params).forEach(([key, value]) => {
    urlObj.searchParams.set(key, value)
  })
  return urlObj.pathname + urlObj.search
}

export function joinURL(base: string, path: string): string {
  if (!base) return path
  if (!path) return base
  if (path.startsWith("http") || path.startsWith("https")) return path
  const hasTrailing = base.endsWith("/")
  const hasLeading = path.startsWith("/")
  if (hasTrailing && hasLeading) {
    return base + path.slice(1)
  }
  if (!hasTrailing && !hasLeading) {
    return base + "/" + path
  }
  return base + path
}

export function getClientToken(config: { cookieName?: string; storageKey?: string }): string | null {
  if (!isClient) return null
  const { cookieName = "SMM_auth_token", storageKey = "token" } = config
  try {
    const cookieToken = getCookie(cookieName)
    if (cookieToken) return cookieToken
    const storageToken = localStorage.getItem(storageKey)
    if (storageToken) return storageToken
    return null
  } catch {
    return null
  }
}

export async function getServerToken(config: { cookieName?: string; storageKey?: string }): Promise<string | null> {
  if (isClient) return null
  const { cookieName = env.NEXT_PUBLIC_AUTH_TOKEN_KEY } = config
  const { cookies } = await import("next/headers")
  const cookieStore = await cookies()
  return cookieStore.get(cookieName || "")?.value || null
}

export function isNextRedirectError(error: unknown): boolean {
  const e = error as Record<string, unknown>
  const d = e?.["digest"]
  if (typeof d === "string" && (d.startsWith("NEXT_REDIRECT;") || d === "NEXT_NOT_FOUND")) return true
  const n = e?.["name"]
  if (typeof n === "string" && n === "NEXT_REDIRECT") return true
  return false
}

/**
 * 从 Next.js headers 中提取客户端真实 IP 地址
 * 优先级：cf-connecting-ip > x-real-ip > x-forwarded-for (第一个)
 *
 * @param headers - Next.js headers() 返回的 Headers 对象
 * @returns 客户端 IP 地址，如果未找到则返回 null
 *
 * @example
 * ```ts
 * import { headers } from 'next/headers'
 * import { getClientIPFromHeaders } from '@repo/http'
 *
 * export default async function Page() {
 *   const headersList = await headers()
 *   const clientIP = getClientIPFromHeaders(headersList)
 *   // 使用 clientIP 作为 getClientIP 的返回值
 * }
 * ```
 */
export function getClientIPFromHeaders(headers: Headers): string | null {
  if (!isServer) return null

  // 优先级：cf-connecting-ip > x-real-ip > x-forwarded-for
  const cfConnectingIP = headers.get("cf-connecting-ip")
  if (cfConnectingIP) {
    return extractIPAddress(cfConnectingIP)
  }

  const realIP = headers.get("x-real-ip")
  if (realIP) {
    return extractIPAddress(realIP)
  }

  const forwardedFor = headers.get("x-forwarded-for")
  if (forwardedFor) {
    // x-forwarded-for 可能包含多个 IP，取第一个
    const firstIP = forwardedFor.split(",")[0]?.trim()
    return extractIPAddress(firstIP)
  }

  return null
}

/**
 * 提取并清理 IP 地址（处理 IPv6 映射的 IPv4 地址）
 */
function extractIPAddress(address: string | null | undefined): string | null {
  if (!address) return null
  // 处理 IPv6 映射的 IPv4 地址（如 ::ffff:192.168.1.1）
  if (address.startsWith("::ffff:")) {
    return address.slice(7)
  }
  return address.trim() || null
}

/**
 * 计算请求体大小（字节）
 */
export function getRequestBodySize(body: BodyInit | null): number {
  if (!body) return 0
  if (typeof body === "string") {
    return new Blob([body]).size
  }
  if (body instanceof FormData) {
    // FormData 大小计算比较复杂，这里返回估算值
    let size = 0
    body.forEach((value) => {
      if (typeof value === "string") {
        size += new Blob([value]).size
      } else if (value instanceof File) {
        size += value.size
      } else {
        size += new Blob([value]).size
      }
    })
    return size
  }
  if (body instanceof Blob) {
    return body.size
  }
  if (body instanceof ArrayBuffer) {
    return body.byteLength
  }
  if (body instanceof URLSearchParams) {
    return new Blob([body.toString()]).size
  }
  return 0
}

/**
 * 格式化字节大小为可读字符串
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * HTTP 请求日志接口
 */
export interface HttpLogInfo {
  /** 时间戳（ISO 格式） */
  timestamp: string
  /** 请求开始时间 */
  startTime: number
  /** 请求结束时间 */
  endTime: number
  /** 耗时（毫秒） */
  duration: number
  /** 请求方法 */
  method: string
  /** 完整 URL */
  url: string
  /** HTTP 状态码 */
  statusCode?: number
  /** 业务码 */
  businessCode?: number
  /** 请求体大小（字节） */
  requestSize?: number
  /** 请求体大小（格式化） */
  requestSizeFormatted?: string
  /** 响应体大小（字节） */
  responseSize?: number
  /** 响应体大小（格式化） */
  responseSizeFormatted?: string
  /** 是否成功 */
  success: boolean
  /** 错误信息 */
  error?: string
  /** 错误类型 */
  errorType?: string
}

/**
 * 记录 HTTP 请求日志
 * 日志格式便于终端过滤检索，使用 [HTTP] 前缀和结构化数据
 * 注意：仅在服务端开发环境输出日志，生产环境和客户端不输出日志
 *
 * @param info - 日志信息
 * @param level - 日志级别
 */
export function logHttpRequest(info: HttpLogInfo, level: LogLevel = "basic"): void {
  // 仅在服务端开发环境输出日志
  // 检查1: 确保不在客户端
  if (isClient || typeof window !== "undefined") return
  // 检查3: 日志级别检查
  if (level === "none") return

  // 使用异步日志，避免阻塞主线程，提升性能
  asyncLog(() => {
    // 在异步回调中再次检查环境，防止在客户端或生产环境执行
    if (isClient || typeof window !== "undefined") return

    const { timestamp, startTime, endTime, duration, method, url, statusCode, success, error } = info

    // 延迟计算格式化时间，只在异步回调中执行
    const startTimeFormatted = new Date(startTime).toISOString()
    const endTimeFormatted = new Date(endTime).toISOString()

    // 提取路径部分用于日志显示（去掉 query 参数）
    const urlPath = extractUrlPath(url)

    // 基础日志：始终输出关键信息（包含时间信息）
    const baseLog: Record<string, unknown> = {
      timestamp,
      startTime: startTimeFormatted,
      endTime: endTimeFormatted,
      duration: `${duration}ms`,
      method,
      url: urlPath, // 只显示路径，不包含 query 参数
      // fullUrl: url, // 完整 URL 单独字段，需要时可查看
      success,
    }

    // 详细日志：包含大小、状态码、业务码等
    if (level === "detailed") {
      baseLog.statusCode = statusCode
      baseLog.businessCode = info.businessCode
      if (info.requestSize !== undefined) {
        baseLog.requestSize = info.requestSize
        baseLog.requestSizeFormatted = info.requestSizeFormatted
      }
      if (info.responseSize !== undefined) {
        baseLog.responseSize = info.responseSize
        baseLog.responseSizeFormatted = info.responseSizeFormatted
      }
      if (error) {
        baseLog.error = error
        baseLog.errorType = info.errorType
      }
    } else if (level === "basic") {
      // 基础级别也包含状态码
      if (statusCode !== undefined) {
        baseLog.statusCode = statusCode
      }
      if (error) {
        baseLog.error = error
      }
    }

    // 使用 [HTTP] 前缀方便 grep 过滤
    // 格式：[HTTP] timestamp method url duration statusCode
    // 在日志前缀中显示开始时间、结束时间和耗时
    const logPrefix = `[HTTP] ${startTimeFormatted} → ${endTimeFormatted} (${duration}ms) ${method} ${urlPath}`
    const statusInfo = statusCode ? ` ${statusCode}` : ""
    const successMark = success ? "✓" : "✗"

    // 统一使用 console.warn，避免 Next.js dev 阶段的错误边界中断渲染
    console.warn(`${logPrefix}${statusInfo} ${successMark}`, baseLog)
  })
}

/**
 * 从 Response 中获取响应体大小
 * 注意：这会消耗 response body，需要先 clone
 */
export async function getResponseSize(response: Response): Promise<number> {
  try {
    const clonedResponse = response.clone()
    const blob = await clonedResponse.blob()
    return blob.size
  } catch {
    // 如果无法获取大小，返回 0
    return 0
  }
}

/**
 * 从 URL 路径中提取 locale
 * URL 格式：/[locale]/...
 * @returns locale 字符串，如果无法获取则返回 null
 */
export function getLocaleFromUrl(): string | null {
  if (!isClient) {
    return "en"
  }
  try {
    const pathname = window.location.pathname
    const segments = pathname.split("/").filter(Boolean)
    const locale = segments[0] || "en"
    return locale
  } catch {
    return "en"
  }
}
